import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  forwardRef,
  inject,
  OnInit,
  output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import {
  FormControlComponent,
  Icon,
  IconComponent,
  PrimengButtonDirective,
  PrimengInputTextDirective,
  PrimengSelectDirective,
  SelectItem,
} from '@web/shared/ui';
import { Button } from 'primeng/button';
import { PasswordModule } from 'primeng/password';
import { TooltipModule } from 'primeng/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Guid,
  isNil,
  isNotEmptyArray,
  isNotNil,
  Nillable,
  once,
  skipOne,
} from '@web/utils';
import { Observable } from 'rxjs';
import { AdminBrandDetailsFormSocialMedia } from '../../models/admin-brand-details-form-social-media';
import { Nullable } from 'primeng/ts-helpers';
import { AdminBrandDetailsFormSocialMediaCollection } from '../../models/admin-brand-details-form-social-media-collection';
import { AdminBrandSocialMediaDataSourceService } from '../../services/admin-brand-social-media-data-source.service';
import { Select } from 'primeng/select';

type SocialMediaFormGroup = FormGroup<{
  socials: FormArray<SocialMediaItemFormGroup>;
}>;

type SocialMediaItemFormGroup = FormGroup<{
  id: FormControl<Nullable<Guid>>;
  url: FormControl<Nullable<string>>;
}>;

@Component({
  selector: 'app-admin-brand-social-media',
  imports: [
    Button,
    CommonModule,
    FormControlComponent,
    IconComponent,
    InputTextModule,
    PasswordModule,
    PrimengButtonDirective,
    PrimengInputTextDirective,
    PrimengSelectDirective,
    ReactiveFormsModule,
    Select,
    TooltipModule,
  ],
  templateUrl: './admin-brand-social-media.component.html',
  styleUrl: './admin-brand-social-media.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AdminBrandSocialMediaComponent),
      multi: true,
    },
  ],
})
export class AdminBrandSocialMediaComponent
  implements ControlValueAccessor, OnInit
{
  private readonly builder = inject(FormBuilder);
  /* prettier-ignore */
  private readonly brandSocialMediaDataSource = inject(AdminBrandSocialMediaDataSourceService);

  /* prettier-ignore */
  public readonly socialMediaChange = output<AdminBrandDetailsFormSocialMediaCollection>();

  protected readonly selectItems$ = this.selectItemsSource();
  protected readonly Icon = Icon;
  protected readonly isNotEmptyArray = isNotEmptyArray;
  protected readonly form = this.buildForm();

  private readonly defaultSocialLink = 'https://example.com';

  private onChange!: (
    value: AdminBrandDetailsFormSocialMediaCollection,
  ) => void;
  private onTouched!: () => void;
  private destroyRef = inject(DestroyRef);

  public ngOnInit(): void {
    this.listenToValueChanges();
  }

  public writeValue(value?: AdminBrandDetailsFormSocialMediaCollection): void {
    if (isNil(value) || isNil(value.items)) {
      return;
    }

    const { items } = value;

    this.socialMediaFormArray().clear({ emitEvent: false });

    items.forEach((item) => {
      this.socialMediaFormArray().push(
        this.createSocialMedialItemFormGrupAndFillByValue(item),
        { emitEvent: false },
      );
    });
  }

  public registerOnChange(
    fn: (value: AdminBrandDetailsFormSocialMediaCollection) => void,
  ): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public handleRemoveClick(index: number): void {
    this.socialMediaFormArray().removeAt(index);
  }

  public handleAddClick(): void {
    this.socialMediaFormArray().push(
      this.createSocialMediaItemFormGroupAndFillByDefaultValue(),
    );
    this.onTouched();
  }

  public setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  private socialMediaFormArray(): FormArray<SocialMediaItemFormGroup> {
    return this.form.controls.socials;
  }

  private toData(): AdminBrandDetailsFormSocialMediaCollection {
    const { socials } = this.form.getRawValue();
    const items = socials
      .map(({ id, url }) => {
        if (isNil(id) || isNil(url)) {
          return null;
        }

        return new AdminBrandDetailsFormSocialMedia(id, url);
      })
      .filter(isNotNil);

    return new AdminBrandDetailsFormSocialMediaCollection(items);
  }

  private listenToValueChanges(): void {
    this.form.valueChanges
      .pipe(skipOne(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const data = this.toData();

        this.onChange(data);
        this.onTouched();
        this.notifySocialMediaChange(data);
      });
  }

  private notifySocialMediaChange(
    data: AdminBrandDetailsFormSocialMediaCollection,
  ): void {
    this.socialMediaChange.emit(data);
  }

  private buildForm(): SocialMediaFormGroup {
    return this.builder.group({
      socials: this.builder.array([
        this.createSocialMediaItemFormGroupAndFillByDefaultValue(),
      ]),
    });
  }

  private createSocialMedialItemFormGrupAndFillByValue(
    item: AdminBrandDetailsFormSocialMedia,
  ): SocialMediaItemFormGroup {
    const formGroup = this.createSocialMediaItemFormGroup();

    formGroup.patchValue(
      {
        id: item.id,
        url: item.url,
      },
      { emitEvent: false },
    );

    return formGroup;
  }

  private createSocialMediaItemFormGroupAndFillByDefaultValue(): SocialMediaItemFormGroup {
    const formGroup = this.createSocialMediaItemFormGroup();

    this.brandSocialMediaDataSource
      .fetchCollection()
      .pipe(once())
      .subscribe((items) => {
        const [first] = items;

        formGroup.patchValue(
          {
            id: first.value,
            url: this.defaultSocialLink,
          },
          { emitEvent: false },
        );
      });

    return formGroup;
  }

  private createSocialMediaItemFormGroup(): SocialMediaItemFormGroup {
    return this.builder.group({
      id: this.builder.control<Nillable<Guid>>(null, [Validators.required]),
      url: this.builder.control<Nillable<string>>(null, [Validators.required]),
    });
  }

  private selectItemsSource(): Observable<SelectItem<Guid>[]> {
    return this.brandSocialMediaDataSource.fetchCollection();
  }
}
